import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import {
  BarPlot,
  BarSeriesType,
  ChartContainerPro,
  ChartsAxisHighlight,
  ChartsClipPath,
  ChartsTooltipContainer,
  ChartsXAxis,
  ChartsYAxis,
} from "@mui/x-charts-pro";
import { ZoomData } from "@mui/x-charts-pro/models";
import { BigTooltip } from "components/BigTooltip";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import { WasteShipmentChartsQuery } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { formatNumber } from "util/formatNumber";

import { SHIPMENTS_STAT_TOOLTIP } from "../../../calculatedStatusCopy";
import {
  AxisThreshold,
  ThresholdReferenceLines,
  ThresholdYAxisIndicators,
} from "../../thresholdAxisIndicators";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { colorMap, formatMonth } from "../utils";
import { AxisTooltip } from "./AxisTooltip";
import { ChartControlsPopover } from "./ChartControlsPopover";
import { ClassificationBars, STATUS_BAR_LABEL_WIDTH } from "./ClassificationBars";
import { useChartDotPositions } from "./hooks/useChartDotPositions";
import { useWasteThresholds } from "./hooks/useWasteThresholds";
import { MonthDataDetails } from "./MonthDataDetails";
import { OverThresholdDot } from "./OverThresholdDot";
import { WasteValueDisplay } from "./WasteValueDisplay";
import { ZoomOverlay } from "./ZoomOverlay";

// The chart's plot area must begin at the same x as the status-bar month cells
// below it, so the left gutter (BarChart margin + y-axis width, and the hit-test
// offset) is derived from the status-bar label column width.
const CHART_LEFT_MARGIN = 20;
const Y_AXIS_WIDTH = STATUS_BAR_LABEL_WIDTH - CHART_LEFT_MARGIN;

type Props = {
  data: WasteShipmentChartsQuery["facilityWasteShipmentCharts"]["wasteShipmentsByMonth"];
  facilityState?: string | null;
};

type ChartControlsState = {
  compareEPAWithVendor: boolean;
  showHazardousWaste: boolean;
  showAcutelyHazardousWaste: boolean;
  showStateRegulatedWaste: boolean;
  thresholdVisibility: Record<string, boolean>;
  showGeneratorStatusBars: boolean;
};

export function HazardousWasteShipmentsGraph({ data, facilityState }: Props) {
  const { facilityId } = useParams<{ facilityId: string }>();
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();

  // Generate unique clip path ID for this chart instance
  const clipPathId = useId();

  // Refs for chart container and overlay positioning
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);

  const createStorageKey = useCallback(
    (baseKey: string) => {
      const keyParts = [baseKey];

      if (facilityId) {
        keyParts.push(facilityId);
      }

      if (deferredFilters.startDate && deferredFilters.endDate) {
        keyParts.push(`${deferredFilters.startDate}-${deferredFilters.endDate}`);
      }

      if (deferredFilters.view) {
        keyParts.push(deferredFilters.view);
      }

      if (deferredFilters.tags && deferredFilters.tags.length > 0) {
        keyParts.push([...deferredFilters.tags].sort().join(","));
      }

      return keyParts.join("-");
    },
    [
      facilityId,
      deferredFilters.startDate,
      deferredFilters.endDate,
      deferredFilters.view,
      deferredFilters.tags,
    ]
  );

  // Get thresholds for the facility's state
  const { thresholds, defaultThresholdVisibility } = useWasteThresholds(facilityState);

  // Check if this state has state-specific waste thresholds
  const showStateRegulatedWaste = stateDoesNotFollowFederalGeneratorStatus(facilityState);

  // Month selection state with localStorage persistence (context-specific)
  const [selectedMonth, setSelectedMonth] = useLocalStorageState<string | null>(
    createStorageKey("hazardous-waste-selected-month"),
    { defaultValue: null }
  );

  // Separate hover state for visual feedback (not persisted)
  const [hoveredMonthIndex, setHoveredMonthIndex] = useState<number | null>(null);
  const deferredSelectedMonth = useDeferredValue(selectedMonth);

  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [monthDetailsCollapsed, setMonthDetailsCollapsed] = useState(true);

  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  // Chart control states with localStorage persistence (context-specific)
  const [chartControls, setChartControls] = useLocalStorageState<ChartControlsState>(
    createStorageKey("waste-shipments-chart-controls"),
    {
      defaultValue: {
        compareEPAWithVendor: true,
        showHazardousWaste: true,
        showAcutelyHazardousWaste: true,
        showStateRegulatedWaste: true,
        showGeneratorStatusBars: true,
        thresholdVisibility: defaultThresholdVisibility,
      },
    }
  );

  // Ensure thresholdVisibility is present (for migration from old format)
  useEffect(() => {
    if (!chartControls.thresholdVisibility) {
      setChartControls({
        ...chartControls,
        thresholdVisibility: defaultThresholdVisibility,
      });
    }
  }, [chartControls, defaultThresholdVisibility, setChartControls]);

  // Adapt the user-visible subset of thresholds into the unit-agnostic shape
  // the shared axis components consume.
  const visibleAxisThresholds = useMemo<AxisThreshold[]>(
    () =>
      thresholds
        .filter((threshold) => chartControls.thresholdVisibility?.[threshold.id])
        .map((threshold) => ({
          id: threshold.id,
          name: threshold.name,
          value: threshold.threshold,
          color: threshold.color,
          displayValue: threshold.displayValue,
        })),
    [thresholds, chartControls.thresholdVisibility]
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Create base dataset with raw values (no transformation)
  const baseDataset = useMemo(() => {
    if (!data?.byMonth) return [];

    return data.byMonth.map((monthData, index) => {
      // Use raw values directly - no transformation
      const vendorAcute = monthData.vendorAcutelyHazardousAmount || 0;
      const vendorRegular = monthData.vendorHazardousAmount || 0;
      const vendorStateRegulated = monthData.vendorStateRegulatedAmount || 0;
      const eManifestAcute = monthData.eManifestAcutelyHazardousAmount || 0;
      const eManifestRegular = monthData.eManifestHazardousAmount || 0;
      const eManifestStateRegulated = monthData.eManifestStateRegulatedAmount || 0;
      return {
        month: formatMonth(monthData.month, index === 0),
        rawMonth: monthData.month, // Keep raw date for selection tracking
        // Direct values for chart display (1:1 scale)
        vendorAcute,
        vendorRegular,
        vendorStateRegulated,
        eManifestAcute,
        eManifestRegular,
        eManifestStateRegulated,
        reportedFederalGeneratorStatus: monthData.reportedFederalGeneratorStatus,
        reportedStateGeneratorStatus: monthData.reportedStateGeneratorStatus,
        calculatedFederalGeneratorStatus: monthData.calculatedFederalGeneratorStatus,
        calculatedStateGeneratorStatus: monthData.calculatedStateGeneratorStatus,
        isOverReportedThreshold: monthData.isOverReportedThreshold,
      };
    });
  }, [data]);
  // For more performant lookups
  const baseDataSetByMonth = useMemo(() => {
    return new Map(
      baseDataset.map((d, index) => [
        d.rawMonth,
        {
          index,
          ...d,
        },
      ])
    );
  }, [baseDataset]);

  // Convert hoveredMonthIndex to hoveredMonth string for ClassificationBars
  const hoveredMonth = useMemo(() => {
    if (hoveredMonthIndex !== null && baseDataset[hoveredMonthIndex]) {
      return baseDataset[hoveredMonthIndex].rawMonth;
    }
    return null;
  }, [hoveredMonthIndex, baseDataset]);

  // Handle chart click for month selection
  const handleItemClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!chartContainerRef.current || !baseDataset.length) return;

      const rect = chartContainerRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const leftAxisOffset = STATUS_BAR_LABEL_WIDTH;
      const chartWidth = rect.width - leftAxisOffset;
      const barWidth = chartWidth / baseDataset.length;
      const barIndex = Math.floor((x - leftAxisOffset) / barWidth);

      if (barIndex >= 0 && barIndex < baseDataset.length) {
        const clickedRawMonth = baseDataset[barIndex]?.rawMonth;
        if (clickedRawMonth) {
          setSelectedMonth(clickedRawMonth);
          setMonthDetailsCollapsed(false);
        }
      }
    },
    [baseDataset, setSelectedMonth]
  );

  // Handle chart hover for visual feedback only
  const handleChartMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!chartContainerRef.current || !baseDataset.length) return;

      const rect = chartContainerRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const leftAxisOffset = STATUS_BAR_LABEL_WIDTH;
      const chartWidth = rect.width - leftAxisOffset;
      const barWidth = chartWidth / baseDataset.length;
      const barIndex = Math.floor((x - leftAxisOffset) / barWidth);

      if (barIndex >= 0 && barIndex < baseDataset.length) {
        // Only update if actually changing to reduce re-renders
        setHoveredMonthIndex((prev) => (prev !== barIndex ? barIndex : prev));
      } else {
        setHoveredMonthIndex((prev) => (prev !== null ? null : prev));
      }
    },
    [baseDataset]
  );

  // Handle hover from ClassificationBars
  const handleMonthHover = useCallback(
    (month: string) => {
      const index = baseDataset.findIndex((d) => d.rawMonth === month);
      setHoveredMonthIndex(index >= 0 ? index : null);
    },
    [baseDataset]
  );

  // Clear hover when mouse leaves chart or classification bars
  const handleMouseLeave = useCallback(() => {
    setHoveredMonthIndex(null);
  }, []);

  // Validate and set selected month - default to first month if stored month is not in dataset
  useEffect(() => {
    if (baseDataset.length > 0) {
      const availableRawMonths = baseDataset.map((d) => d.rawMonth);
      const firstRawMonth = availableRawMonths[0];

      if (!selectedMonth || !availableRawMonths.includes(selectedMonth)) {
        setSelectedMonth(firstRawMonth);
      }
    }
  }, [baseDataset, selectedMonth, setSelectedMonth]);

  // Get the selected month but deferred to improve performance
  const selectedMonthData = useMemo(() => {
    if (!deferredSelectedMonth || !baseDataSetByMonth) return null;
    return baseDataSetByMonth.get(deferredSelectedMonth) || null;
  }, [deferredSelectedMonth, baseDataSetByMonth]);

  // Check which data types are available
  const {
    hasRegularEManifestData,
    hasRegularVendorData,
    hasAcuteEManifestData,
    hasAcuteVendorData,
    hasStateRegulatedVendorData,
    hasStateRegulatedEManifestData,
  } = useMemo(() => {
    if (!data?.byMonth) {
      return {
        hasRegularEManifestData: false,
        hasRegularVendorData: false,
        hasAcuteEManifestData: false,
        hasAcuteVendorData: false,
        hasStateRegulatedVendorData: false,
        hasStateRegulatedEManifestData: false,
      };
    }

    return {
      hasRegularEManifestData: data.byMonth.some(
        (monthData) => (monthData.eManifestHazardousAmount || 0) > 0
      ),
      hasRegularVendorData: data.byMonth.some(
        (monthData) => (monthData.vendorHazardousAmount || 0) > 0
      ),
      hasAcuteEManifestData: data.byMonth.some(
        (monthData) => (monthData.eManifestAcutelyHazardousAmount || 0) > 0
      ),
      hasAcuteVendorData: data.byMonth.some(
        (monthData) => (monthData.vendorAcutelyHazardousAmount || 0) > 0
      ),
      hasStateRegulatedVendorData: data.byMonth.some(
        (monthData) => (monthData.vendorStateRegulatedAmount || 0) > 0
      ),
      hasStateRegulatedEManifestData: data.byMonth.some(
        (monthData) => (monthData.eManifestStateRegulatedAmount || 0) > 0
      ),
    };
  }, [data?.byMonth]);

  // Create stacked series configuration
  const stackedSeries: BarSeriesType[] = useMemo(() => {
    const series: BarSeriesType[] = [];

    // VENDOR STACK (Always appears on the left)
    // Bottom layer: Vendor Acute values (darker colors)
    if (chartControls.showAcutelyHazardousWaste && hasAcuteVendorData) {
      series.push({
        type: "bar",
        id: "vendorAcute",
        dataKey: "vendorAcute",
        stack: "vendor",
        color: deepOrange[800], // Dark orange for acute vendor
        valueFormatter: (value) => {
          return `Vendor Acute: (${formatNumber(value)} lbs)`;
        },
      });
    }

    // Middle layer: Vendor Hazardous values (lighter colors)
    if (chartControls.showHazardousWaste && hasRegularVendorData) {
      series.push({
        type: "bar",
        id: "vendorRegular",
        dataKey: "vendorRegular",
        stack: "vendor",
        color: colorMap["Vendor Hazardous"], // Light orange for hazardous vendor
        valueFormatter: (value) => {
          return `Vendor Hazardous: (${formatNumber(value)} lbs)`;
        },
      });
    }

    // Top layer: Vendor State regulated waste (yellow colors) - only for states with state thresholds
    if (
      showStateRegulatedWaste &&
      chartControls.showStateRegulatedWaste &&
      hasStateRegulatedVendorData
    ) {
      series.push({
        type: "bar",
        id: "vendorStateRegulated",
        dataKey: "vendorStateRegulated",
        stack: "vendor",
        color: colorMap["Vendor State Regulated"], // Yellow for state regulated vendor
        valueFormatter: (value) => {
          return `Vendor State Regulated: (${formatNumber(value)} lbs)`;
        },
      });
    }

    // e‑Manifest STACK (Always appears on the right when comparing)
    // Bottom layer: e‑Manifest Acute values (darker colors)
    if (
      chartControls.showAcutelyHazardousWaste &&
      hasAcuteEManifestData &&
      chartControls.compareEPAWithVendor
    ) {
      series.push({
        type: "bar",
        id: "eManifestAcute",
        dataKey: "eManifestAcute",
        stack: "eManifest",
        color: colorMap["e‑Manifest Acute"], // Dark grey for acute e‑Manifest
        valueFormatter: (value) => {
          return `e‑Manifest Acute: (${formatNumber(value)} lbs)`;
        },
      });
    }

    // Middle layer: e‑Manifest Hazardous values (lighter colors)
    if (
      chartControls.showHazardousWaste &&
      hasRegularEManifestData &&
      chartControls.compareEPAWithVendor
    ) {
      series.push({
        type: "bar",
        id: "eManifestRegular",
        dataKey: "eManifestRegular",
        stack: "eManifest",
        color: colorMap["e‑Manifest Hazardous"], // Light grey for hazardous e‑Manifest
        valueFormatter: (value) => {
          return `e‑Manifest Hazardous: (${formatNumber(value)} lbs)`;
        },
      });
    }

    // Top layer: e‑Manifest State regulated waste (blue colors) - only for states with state thresholds
    if (
      showStateRegulatedWaste &&
      chartControls.showStateRegulatedWaste &&
      hasStateRegulatedEManifestData &&
      chartControls.compareEPAWithVendor
    ) {
      series.push({
        type: "bar",
        id: "eManifestStateRegulated",
        dataKey: "eManifestStateRegulated",
        stack: "eManifest",
        color: colorMap["e‑Manifest State Regulated"], // Blue for state regulated e‑Manifest
        valueFormatter: (value) => {
          return `e‑Manifest State Regulated: (${formatNumber(value)} lbs)`;
        },
      });
    }

    return series;
  }, [
    showStateRegulatedWaste,
    chartControls.showHazardousWaste,
    chartControls.showAcutelyHazardousWaste,
    chartControls.showStateRegulatedWaste,
    chartControls.compareEPAWithVendor,
    hasRegularEManifestData,
    hasRegularVendorData,
    hasAcuteEManifestData,
    hasAcuteVendorData,
    hasStateRegulatedVendorData,
    hasStateRegulatedEManifestData,
  ]);

  // Calculate max value for initial y-axis view
  const maxValue = useMemo(() => {
    if (baseDataset.length === 0) return 100;

    let max = 0;
    baseDataset.forEach((dataPoint) => {
      // Calculate vendor stack total based on visible series
      let vendorTotal = 0;
      if (chartControls.showHazardousWaste) {
        vendorTotal += dataPoint.vendorRegular;
      }
      if (chartControls.showAcutelyHazardousWaste) {
        vendorTotal += dataPoint.vendorAcute;
      }
      if (showStateRegulatedWaste && chartControls.showStateRegulatedWaste) {
        vendorTotal += dataPoint.vendorStateRegulated;
      }
      max = Math.max(max, vendorTotal);

      // Only include eManifest data if comparing with EPA and respective series are visible
      if (chartControls.compareEPAWithVendor) {
        let eManifestTotal = 0;
        if (chartControls.showHazardousWaste) {
          eManifestTotal += dataPoint.eManifestRegular;
        }
        if (chartControls.showAcutelyHazardousWaste) {
          eManifestTotal += dataPoint.eManifestAcute;
        }
        if (showStateRegulatedWaste && chartControls.showStateRegulatedWaste) {
          eManifestTotal += dataPoint.eManifestStateRegulated;
        }
        max = Math.max(max, eManifestTotal);
      }
    });

    // Ensure we have a reasonable minimum value when no data is visible
    if (max === 0) {
      max = 100;
    }

    // Apply padding in sqrt space, then convert back to data space
    // This ensures the visual padding is consistent with the sqrt scale
    const sqrtMax = Math.sqrt(max);
    const paddedSqrtMax = sqrtMax * 1.15; // 15% visual padding in sqrt space
    return paddedSqrtMax * paddedSqrtMax; // Square it back to data space
  }, [
    baseDataset,
    showStateRegulatedWaste,
    chartControls.compareEPAWithVendor,
    chartControls.showHazardousWaste,
    chartControls.showAcutelyHazardousWaste,
    chartControls.showStateRegulatedWaste,
  ]);

  // Create final dataset for bar charts
  const dataset = useMemo(() => {
    return baseDataset.map((dataPoint) => ({
      month: dataPoint.month,
      vendorAcute: dataPoint.vendorAcute,
      vendorRegular: dataPoint.vendorRegular,
      vendorStateRegulated: dataPoint.vendorStateRegulated,
      eManifestAcute: dataPoint.eManifestAcute,
      eManifestRegular: dataPoint.eManifestRegular,
      eManifestStateRegulated: dataPoint.eManifestStateRegulated,
    }));
  }, [baseDataset]);

  // Use custom hook for dot positioning
  const { dotPositions, updateDotPositions } = useChartDotPositions({
    baseDataset,
    chartControls,
    chartContainerRef,
  });

  // Extract just the status data for the tooltip. Parallel to `data.byMonth`, so
  // the tooltip can look a bar up by its `dataIndex`; carries each month's own
  // date so the tooltip titles the bar it's actually over.
  const monthStatusData = useMemo(
    () =>
      data?.byMonth?.map((month) => ({
        month: month.month,
        reportedGeneratorStatus: month.reportedFederalGeneratorStatus,
        isOverReportedThreshold: month.isOverReportedThreshold,
      })) || [],
    [data]
  );

  // Memoize the highlighted axis configuration
  // Only show highlight when actively hovering
  const highlightedAxis = useMemo(
    () =>
      hoveredMonthIndex !== null
        ? [
            {
              axisId: "x-axis",
              dataIndex: hoveredMonthIndex,
            },
          ]
        : [], // Empty array means no highlight
    [hoveredMonthIndex]
  );

  // Handle zoom changes with consistent zoom behavior and origin anchoring
  const handleZoomChange = useCallback(
    (zoomData: ZoomData[]) => {
      zoomData.forEach((zoom) => {
        if (zoom.axisId === "y-axis") {
          // Always start from 0 (origin anchored)
          // overrides the default where the zoom starts at the mouse position
          // for sqrt scale, this keeps the zoom change consistent regardless of the mouse position
          const originalStart = zoom.start;
          const originalEnd = zoom.end;
          const originalRange = originalEnd - originalStart;

          // Force start to 0 and adjust end to maintain consistent zoom behavior
          zoom.start = 0;

          // Calculate the zoom level based on the range
          // Smaller range = more zoomed in
          const zoomLevel = originalRange;

          // Apply consistent zoom from origin
          zoom.end = zoomLevel;
        }
      });

      // Update dot positions after zoom
      setTimeout(updateDotPositions, 50);
    },
    [updateDotPositions]
  );

  return (
    <>
      {/* Wrap + flex gap (not margin-based `spacing`) so the row of fixed-
          min-width stats reflows at reduced effective viewport widths instead
          of forcing a horizontal page scroll. */}
      <Stack
        direction={{ sm: "column", md: "row" }}
        flexWrap="wrap"
        marginBottom={{ sm: 2, md: 0 }}
        flex={1}
        sx={{ columnGap: { md: 10 }, rowGap: 0 }}
      >
        <WasteValueDisplay label="Total waste" value={data.totalAllHazardousAmount} />
        <WasteValueDisplay label="Hazardous waste" value={data.totalHazardousAmount} />
        <WasteValueDisplay
          label="Acutely hazardous waste"
          value={data.totalAcutelyHazardousAmount}
        />
        {showStateRegulatedWaste && (
          <WasteValueDisplay label="State regulated waste" value={data.totalStateRegulatedAmount} />
        )}
        <WasteValueDisplay
          label="Highest status from shipments"
          value={federalWasteGeneratorStatusToLabel(data.highestCalculatedGeneratorStatus ?? null)}
          tooltip={SHIPMENTS_STAT_TOOLTIP}
        />
        {showStateRegulatedWaste && (
          <WasteValueDisplay
            label="Highest state status from shipments"
            value={stateWasteGeneratorStatusToLabel(
              data.highestCalculatedStateGeneratorStatus ?? null
            )}
            tooltip={SHIPMENTS_STAT_TOOLTIP}
          />
        )}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1">Hazardous waste shipments in lbs</Typography>
          <BigTooltip
            title={
              <Typography variant="caption">
                Based on data ingested by Encamp, see how your historical RCRA hazardous and state
                regulated waste shipments relate to generator status thresholds. If a threshold is
                exceeded in a calendar month, you may need to file an update notification with the
                EPA. If both vendor and e‑Manifest data is present for a manifest, we will display
                vendor data.
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlined color="disabled" />
            </IconButton>
          </BigTooltip>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch checked={zoomEnabled} onChange={(e) => setZoomEnabled(e.target.checked)} />
            }
            label="Enable zoom"
          />
          <Button variant="text" endIcon={<MoreVert />} onClick={handleClick}>
            Chart Controls
          </Button>
        </Stack>
      </Stack>
      <Box>
        <Box sx={{ height: "300px", width: "100%", position: "relative", mb: 0 }}>
          <Box
            ref={chartContainerRef}
            onMouseMove={handleChartMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleItemClick}
            sx={{
              height: "100%",
              width: "100%",
              cursor: "pointer",
            }}
          >
            <ChartContainerPro
              // Re-renders the chart when enabling/disabling zoom
              key={`chart-${zoomEnabled}`}
              dataset={dataset}
              series={stackedSeries}
              sx={{
                zIndex: 300,
                "& .MuiChartsAxisHighlight-root": {
                  fill: (theme) => theme.palette.grey[500],
                  fillOpacity: 0.2,
                  zIndex: 1000,
                },
              }}
              highlightedAxis={highlightedAxis}
              onZoomChange={handleZoomChange}
              xAxis={[
                {
                  id: "x-axis",
                  scaleType: "band",
                  dataKey: "month",
                  categoryGapRatio: 0.3,
                  barGapRatio: 0.1,
                  height: 40,
                },
              ]}
              yAxis={[
                {
                  id: "y-axis",
                  scaleType: "sqrt",
                  min: 0,
                  max: maxValue,
                  zoom: zoomEnabled,
                  valueFormatter: (value) => formatNumber(value),
                  width: Y_AXIS_WIDTH,
                },
              ]}
              margin={{
                left: CHART_LEFT_MARGIN,
                right: 20,
                top: 0,
                bottom: 0,
              }}
              height={300}
            >
              {/* Clipped content - bars, tooltips, and highlights stay within chart bounds */}
              <g clipPath={`url(#${clipPathId})`}>
                <BarPlot
                  slotProps={{
                    bar: {
                      rx: 3,
                      ry: 3,
                      style: {
                        strokeWidth: 1,
                        stroke: "white",
                      },
                    },
                  }}
                />
                <ChartsAxisHighlight x="band" />
              </g>

              {/* Reference lines component - rendered after bars so they appear on top */}
              <ThresholdReferenceLines thresholds={visibleAxisThresholds} />

              {/* Axes and tooltips */}
              <ChartsXAxis labelStyle={{ zIndex: 300 }} />
              <ChartsYAxis />

              {/* Y-axis threshold indicators - rendered after axes to appear on top */}
              <ThresholdYAxisIndicators thresholds={visibleAxisThresholds} />
              {hoveredMonthIndex != null && (
                <ChartsTooltipContainer trigger="axis">
                  <AxisTooltip
                    monthStatusData={monthStatusData}
                    includeStateRegulatedWaste={showStateRegulatedWaste}
                  />
                </ChartsTooltipContainer>
              )}

              {/* Define the clip path */}
              <ChartsClipPath id={clipPathId} />
            </ChartContainerPro>
          </Box>

          {/* HTML Overlay for threshold indicator dots */}
          <Box
            ref={overlayContainerRef}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 500, // Very high z-index to ensure it appears above SVG elements
            }}
          >
            {dotPositions.map((dot, index) => (
              <Box
                key={`threshold-dot-${dot.month}-${index}`}
                sx={{
                  position: "absolute",
                  left: dot.x,
                  top: dot.y,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <OverThresholdDot title={`Over Reported Threshold: ${dot.month}`} />
              </Box>
            ))}
          </Box>

          <ZoomOverlay visible={zoomEnabled} />
        </Box>
        {/* Classification bars */}
        {data.byMonth.length > 0 && chartControls.showGeneratorStatusBars && (
          <ClassificationBars
            data={data}
            hoveredMonth={hoveredMonth}
            onMonthHover={handleMonthHover}
            onMonthLeave={handleMouseLeave}
            onMonthClick={(month) => {
              setSelectedMonth(month);
              setMonthDetailsCollapsed(false);
            }}
            showState={showStateRegulatedWaste}
          />
        )}
      </Box>

      <Stack spacing={2}>
        <Box>
          <Button
            onClick={() => setMonthDetailsCollapsed(!monthDetailsCollapsed)}
            endIcon={monthDetailsCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          >
            {monthDetailsCollapsed ? "View Month Details" : "Collapse Month Details"}
          </Button>
        </Box>
        <Collapse in={!monthDetailsCollapsed}>
          {selectedMonthData ? (
            <MonthDataDetails
              vendorHazardousAmount={selectedMonthData?.vendorRegular}
              eManifestHazardousAmount={selectedMonthData?.eManifestRegular}
              vendorAcutelyHazardousAmount={selectedMonthData?.vendorAcute}
              eManifestAcutelyHazardousAmount={selectedMonthData?.eManifestAcute}
              vendorStateRegulatedAmount={selectedMonthData?.vendorStateRegulated}
              eManifestStateRegulatedAmount={selectedMonthData?.eManifestStateRegulated}
              reportedFederalGeneratorStatus={selectedMonthData?.reportedFederalGeneratorStatus}
              reportedStateGeneratorStatus={selectedMonthData?.reportedStateGeneratorStatus}
              calculatedFederalGeneratorStatus={selectedMonthData?.calculatedFederalGeneratorStatus}
              calculatedStateGeneratorStatus={selectedMonthData?.calculatedStateGeneratorStatus}
              isOverReportedThreshold={selectedMonthData?.isOverReportedThreshold}
              month={selectedMonthData?.rawMonth}
              showStateRegulatedWaste={showStateRegulatedWaste}
              facilityState={facilityState}
              availableMonths={data.byMonth.map((m) => m.month)}
              onMonthChange={setSelectedMonth}
            />
          ) : null}
        </Collapse>
      </Stack>

      <ChartControlsPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        compareEPAWithVendor={chartControls.compareEPAWithVendor}
        setCompareEPAWithVendor={(value) =>
          setChartControls({ ...chartControls, compareEPAWithVendor: value })
        }
        showHazardousWaste={chartControls.showHazardousWaste}
        setShowHazardousWaste={(value) =>
          setChartControls({ ...chartControls, showHazardousWaste: value })
        }
        showAcutelyHazardousWaste={chartControls.showAcutelyHazardousWaste}
        setShowAcutelyHazardousWaste={(value) =>
          setChartControls({
            ...chartControls,
            showAcutelyHazardousWaste: value,
          })
        }
        showStateRegulatedWaste={chartControls.showStateRegulatedWaste}
        setShowStateRegulatedWaste={(value) =>
          setChartControls({
            ...chartControls,
            showStateRegulatedWaste: value,
          })
        }
        hasStateWasteThresholds={showStateRegulatedWaste}
        thresholds={thresholds}
        thresholdVisibility={chartControls.thresholdVisibility || {}}
        setThresholdVisibility={(id, visible) =>
          setChartControls({
            ...chartControls,
            thresholdVisibility: {
              ...chartControls.thresholdVisibility,
              [id]: visible,
            },
          })
        }
        showGeneratorStatusBars={chartControls.showGeneratorStatusBars}
        setShowGeneratorStatusBars={(value) =>
          setChartControls({ ...chartControls, showGeneratorStatusBars: value })
        }
        facilityState={facilityState}
      />
    </>
  );
}
